import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useEffect, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { AppForm, AppButton, FillTheFieldErrorMessage } from "@/shared/Components";
import { appRouterPaths } from "@/services/navigation";
import { useAuth } from "@/services/auth";
import { installationConfig } from "@/shared/constants/installation-config";
import type { LoginFormValues } from "../types";
import { FormHeader } from "../../FormHeader";
import { getLoginSchema } from "../schema";
import { AuthFormFields } from "./AuthFormFields";
import styles from "./styles.module.scss";

interface Props {
  className?: string;
}

export const AuthForm: FC<Props> = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const formApi = useForm({ resolver: yupResolver(getLoginSchema()) });
  const { loginHandler, loginError } = useAuth();

  const onSubmit = (data: LoginFormValues) => {
    if (!data.password || !data.username) return;
    loginHandler({ password: data.password, username: data.username });
  };

  const onClickForgotPassword = () => {
    navigate(appRouterPaths.passwordRecovery);
  };

  useEffect(() => {
    if (loginError) {
      formApi.setError("username", { message: " " });
      formApi.setError("password", { message: " " });
    }
  }, [loginError]);

  return (
    <div className={styles.root}>
      <AppForm formApi={formApi} onSubmit={onSubmit}>
        <FormHeader
          title={t("logInPage.welcomeBack", {
            value: installationConfig.name,
          })}
          subtitle={t("logInPage.doNotHaveAccountYet")}
          buttonText={t("logInPage.signUp")}
          path={appRouterPaths.signUp}
        />
        {loginError && <p className={styles.error}>{loginError}</p>}
        <FillTheFieldErrorMessage name={"loginData"} />
        <AuthFormFields />
        <div className={styles["buttons"]}>
          <AppButton htmlType="submit" type="primary">
            {t("logInPage.login")}
          </AppButton>
          <AppButton
            type="text"
            className={styles["forgot-button"]}
            onClick={onClickForgotPassword}
          >
            {t("logInPage.forgotPassword")}
          </AppButton>
        </div>
      </AppForm>
    </div>
  );
};
